import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "utils/api";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { MenuItem, Select, FormControl, InputLabel } from "@mui/material";

function EditVideo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    videoType: "",
    description: "",
    videoLink: "",
  });

  useEffect(() => {
    fetchVideoData();
  }, [id]);

  const fetchVideoData = async () => {
    try {
      setFetchLoading(true);
      const response = await api.get(`/videos/${id}`);
      if (response.data.success) {
        const video = response.data.data;
        setFormData({
          title: video.title || "",
          videoType: video.videoType || "",
          description: video.description || "",
          videoLink: video.videoLink || "",
        });
      } else {
        toast.error("Failed to fetch video data");
        navigate("/allvideos");
      }
    } catch (error) {
      console.error("Error fetching video:", error);
      toast.error(error.response?.data?.message || "Failed to fetch video data");
      navigate("/allvideos");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter video title");
      return;
    }
    if (!formData.videoType) {
      toast.error("Please select video type");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter description");
      return;
    }
    if (!formData.videoLink.trim()) {
      toast.error("Please enter video link");
      return;
    }

    try {
      setLoading(true);
      const response = await api.put(`/videos/${id}`, formData);

      if (response.data.success) {
        toast.success(response.data.message || "Video updated successfully");
        navigate("/allvideos");
      } else {
        toast.error(response.data.message || "Failed to update video");
      }
    } catch (error) {
      console.error("Error updating video:", error);
      toast.error(error.response?.data?.message || "Failed to update video");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress color="info" />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h5" fontWeight="medium" mb={3}>
                  Edit Video
                </ArgonTypography>

                <ArgonBox component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Title */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Title *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        type="text"
                        placeholder="Enter video title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* Video Type */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Type *
                        </ArgonTypography>
                      </ArgonBox>
                      <FormControl fullWidth>
                        <Select
                          name="videoType"
                          value={formData.videoType}
                          onChange={handleChange}
                          displayEmpty
                          sx={{
                            height: "44px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#d2d6da",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select video type
                          </MenuItem>
                          <MenuItem value="EDUCATIONAL">Educational</MenuItem>
                          <MenuItem value="TUTORIAL">Tutorial</MenuItem>
                          <MenuItem value="PRACTICAL">Practical</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Video Link */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Link *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        type="text"
                        placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                        name="videoLink"
                        value={formData.videoLink}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Description *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        multiline
                        rows={4}
                        placeholder="Enter video description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                        <ArgonButton
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate("/allvideos")}
                          disabled={loading}
                        >
                          Cancel
                        </ArgonButton>
                        <ArgonButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Video"}
                        </ArgonButton>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditVideo;
